import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faFlask,
  faSliders,
  faShieldHalved,
  faArrowsRotate,
  faRocket,
  faSatelliteDish,
  faChartLine,
  faPenRuler,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PipelineStrip from '../../components/layout/PipelineStrip';
import PublicFooter from '../../components/layout/PublicFooter';

import QSLEditorMock from './research-pipeline/QSLEditorMock';
import BaselinePerformanceMock from './research-pipeline/BaselinePerformanceMock';
import ParameterStabilityMock from './research-pipeline/ParameterStabilityMock';
import WFAWindowMock from './research-pipeline/WFAWindowMock';
import DeploymentReadinessMock from './research-pipeline/DeploymentReadinessMock';
import AdaptiveFlowMonitorMock from './research-pipeline/AdaptiveFlowMonitorMock';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ═══════════════════ Pipeline stage data ═══════════════ */
const pipelineStages = [
  { icon: faPenRuler, label: 'Strategy Code', short: 'QSL' },
  { icon: faFlask, label: 'Baseline Backtest', short: 'Backtest' },
  { icon: faSliders, label: 'Parameter Optimization', short: 'Optimize' },
  { icon: faShieldHalved, label: 'Stability Analysis', short: 'Stability' },
  { icon: faChartLine, label: 'Cross-Asset Validation', short: 'Cross-Asset' },
  { icon: faArrowsRotate, label: 'Walk-Forward Testing', short: 'WFA' },
  { icon: faRocket, label: 'Deployment', short: 'Deploy' },
  { icon: faSatelliteDish, label: 'Adaptive Flow', short: 'Monitor' },
];

/* ═══════════════════════════ PAGE ═════════════════════════════════════ */

const ResearchPipelinePage: FC = () => {
  usePageMeta({ title: 'Research Pipeline', description: 'The Quanthop research pipeline. Every strategy follows a structured path from idea to deployment with measurable gates at each stage.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ 1. HERO ════════════════ */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="max-w-[1440px] mx-auto relative z-10">
          {/* Center copy */}
          <div className="max-w-[680px] mx-auto text-center mb-16">
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
              Research Pipeline
            </p>

            <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
              The Quanthop research pipeline
            </h1>

            <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
              Every strategy follows the same structured path from idea to
              deployment. Each stage produces measurable outputs, and each
              gate must pass before the strategy advances.
            </p>

            <p className="text-[14px] text-white/60 italic mb-8">
              No shortcuts. No stages skipped. Every result is reproducible.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
              <a href="#pipeline" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
                View the full pipeline <span className="text-gray-600">&rarr;</span>
              </a>
            </div>
          </div>

          {/* Pipeline visual -- horizontal stage diagram */}
          <div className="max-w-[1440px] mx-auto">
            <div className="flex items-center justify-center gap-2 flex-wrap font-mono text-[10px] pb-2">
              {pipelineStages.map((stage, i) => (
                <span key={stage.label} className="flex items-center gap-1">
                  <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-gray-300">
                    <FontAwesomeIcon icon={stage.icon} className="text-[#9d4edd]/60 text-[9px]" />
                    <span className="hidden sm:inline">{stage.label}</span>
                    <span className="sm:hidden">{stage.short}</span>
                  </span>
                  {i < pipelineStages.length - 1 && <span className="hidden md:inline text-gray-600 text-[10px] mx-0.5">&rarr;</span>}
                </span>
              ))}
            </div>
            <p className="text-center text-[11px] text-gray-500 mt-4 font-mono">
              8 stages &middot; Pass/fail gates at every transition &middot; Full audit trail
            </p>
          </div>
        </div>
      </section>

      <PipelineStrip />

      {/* ════════════════ 2. PIPELINE OVERVIEW ════════════════ */}
      <section id="pipeline" className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Pipeline stages
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[600px] mx-auto">
              Each stage produces outputs that feed downstream analysis. Gates
              prevent strategies from advancing until validation criteria are met.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                num: '01',
                icon: faPenRuler,
                title: 'Strategy Development',
                desc: 'Strategies are written in QSL (Quanthop Strategy Language) with a structured lifecycle: define parameters, initialise indicators, execute bar-by-bar logic. Version-controlled code ensures every research iteration is reproducible.',
                output: 'Validated QSL strategy code with parameter schema',
                gate: 'Code must parse and validate without errors',
              },
              {
                num: '02',
                icon: faFlask,
                title: 'Baseline Backtest',
                desc: 'Initial performance evaluation against historical data. Produces core metrics including return, Sharpe ratio, drawdown, win rate, and expectancy. Establishes the performance baseline before any optimization.',
                output: 'Backtest statistics, equity curve, trade log',
                gate: 'Strategy must produce statistically meaningful trade count',
              },
              {
                num: '03',
                icon: faSliders,
                title: 'Parameter Optimization',
                desc: 'Systematic exploration of the parameter search space. The engine evaluates every combination and scores neighbourhood stability, not just peak performance. Identifies robust parameter regions rather than isolated optima.',
                output: 'Parameter surface, stability scores, cluster analysis',
                gate: 'Stable region must exist with consistent performance',
                link: '/features/optimization',
                linkText: 'Parameter Optimization details',
              },
              {
                num: '04',
                icon: faShieldHalved,
                title: 'Stability Analysis',
                desc: 'Extracts contiguous parameter regions and scores their robustness. Evaluates performance degradation from optimal to neighbouring parameter sets. Identifies whether the strategy depends on precise tuning or survives perturbation.',
                output: 'Stability score, region boundaries, degradation profile',
                gate: 'Stability score must exceed minimum threshold',
              },
              {
                num: '05',
                icon: faChartLine,
                title: 'Cross-Asset Validation',
                desc: 'Tests whether the optimized parameters generalize across multiple assets. A strategy that works on one asset but collapses on correlated instruments is likely overfit to noise rather than capturing a durable pattern.',
                output: 'Multi-asset performance matrix, consistency score',
                gate: 'Performance must hold across minimum asset count',
                link: '/features/validation',
                linkText: 'Validation pipeline details',
              },
              {
                num: '06',
                icon: faArrowsRotate,
                title: 'Walk-Forward Testing',
                desc: 'Rolling out-of-sample validation with anchored or sliding windows. Each window re-optimizes parameters on in-sample data, then evaluates on unseen out-of-sample data. Walk-Forward Efficiency measures how much in-sample performance survives.',
                output: 'OOS returns, WFE score, window-by-window breakdown',
                gate: 'WFE and OOS Sharpe must meet defined thresholds',
              },
              {
                num: '07',
                icon: faRocket,
                title: 'Deployment',
                desc: 'Strategies that pass all validation gates enter deployment readiness. The validated parameter set, research lineage, and performance envelope are preserved and carried forward into live monitoring.',
                output: 'Deployment-ready configuration with validation provenance',
                gate: 'All upstream gates must be passed',
              },
              {
                num: '08',
                icon: faSatelliteDish,
                title: 'Adaptive Flow Monitoring',
                desc: 'Continuous post-deployment validation using the same statistical framework applied during research. Monitors live performance against the walk-forward baseline, detects structural drift, and triggers controlled re-optimization when degradation is confirmed.',
                output: 'Health score, deviation alerts, re-optimization triggers',
                gate: 'Automated state transitions based on statistical thresholds',
                link: '/features/continuous-validation',
                linkText: 'Continuous Validation details',
              },
            ].map((stage) => (
              <div
                key={stage.num}
                className="flex gap-5 items-start py-5 px-6 rounded-lg border border-gray-200/80 bg-white shadow-sm"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#f3e8ff] flex items-center justify-center">
                  <FontAwesomeIcon icon={stage.icon} className="text-sm text-[#9d4edd]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-[10px] font-mono text-[#9d4edd]/50">{stage.num}</span>
                    <h3 className="text-[14px] font-semibold text-gray-950">{stage.title}</h3>
                  </div>
                  <p className="text-[13px] text-gray-500 leading-[1.6] mb-2">{stage.desc}</p>
                  <p className="text-[12px] text-gray-400 font-mono mb-1">Output: <span className="italic">{stage.output}</span></p>
                  <p className="text-[11px] text-[#9d4edd]/70 font-mono">Gate: {stage.gate}</p>
                  {stage.link && (
                    <Link to={stage.link} className="inline-flex items-center gap-1.5 mt-2 text-[12px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
                      {stage.linkText} <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 3. STRATEGY DEVELOPMENT (QSL) ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 01
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Strategy code that is version-controlled and reproducible
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Strategies are written in QSL with a structured lifecycle:
              define parameters, initialise indicators, execute bar-by-bar
              logic. The IDE validates code in real-time, tracks every
              version, and enforces a parameter schema that feeds directly
              into downstream optimization.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">QSL provides:</p>
            <div className="space-y-2 mb-6">
              {[
                'Structured parameter definitions with optimization bounds',
                'Built-in indicator library (EMA, SMA, RSI, MACD, Bollinger)',
                'Order API with market, limit, and close operations',
                'Version timeline with non-destructive restore',
                'Real-time validation with error diagnostics',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/features/workspace" className="inline-flex items-center gap-1.5 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
              Research Workspace details <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
            </Link>
          </div>

          <QSLEditorMock />
        </div>
      </section>

      {/* ════════════════ 4. BASELINE TESTING ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <BaselinePerformanceMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 02
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Baseline backtest establishes initial performance
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Before any optimization, the strategy runs against historical
              data with default parameters. This baseline measures raw
              strategy behaviour and establishes whether the core logic has
              merit before parameter tuning begins.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Metrics are structured to prevent outcome-anchoring. Sharpe
              ratio, win rate, and expectancy are presented as primary
              measures. Total return is deliberately demoted to a footnote.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Baseline outputs:</p>
            <div className="space-y-2 mb-6">
              {[
                'Risk-adjusted performance metrics',
                'Trade distribution and payoff analysis',
                'Drawdown profile with recovery timing',
                'Regime exposure breakdown',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Trade count must be statistically meaningful
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 5. PARAMETER OPTIMIZATION ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stages 03 &ndash; 04
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Parameter search with stability scoring
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The optimization engine evaluates every parameter combination
              and scores the neighbourhood around each set. Strategies that
              depend on precise tuning are flagged. Strategies with broad,
              stable performance regions advance.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Stability analysis extracts contiguous parameter regions and
              measures how performance degrades as parameters shift. This
              directly predicts how the strategy will behave when deployed
              with parameters that differ slightly from the tested optimum.
            </p>

            <div className="space-y-2 mb-6">
              {[
                'Full parameter surface exploration',
                'Neighbourhood stability scoring',
                'Contiguous region extraction',
                'Multi-asset consistency check',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/features/optimization" className="inline-flex items-center gap-1.5 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
              Parameter Optimization details <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
            </Link>
          </div>

          <ParameterStabilityMock />
        </div>
      </section>

      {/* ════════════════ 6. VALIDATION ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <WFAWindowMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stages 05 &ndash; 06
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Cross-asset validation and walk-forward testing
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Optimized parameters are tested across correlated assets to
              verify generalization. Walk-forward analysis then evaluates
              performance on truly unseen data through rolling in-sample
              and out-of-sample windows.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Walk-Forward Efficiency (WFE) measures the ratio of
              out-of-sample to in-sample performance. A high WFE indicates
              that optimized parameters retain their edge when applied to
              new market conditions.
            </p>

            <div className="space-y-2 mb-6">
              {[
                'Rolling anchored or sliding window configurations',
                'Per-window parameter re-optimization',
                'Out-of-sample performance measurement',
                'WFE and degradation scoring',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/features/validation" className="inline-flex items-center gap-1.5 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
              Validation pipeline details <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════ 7. DEPLOYMENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 07
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Deployment preserves the full research record
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              When a strategy passes all validation gates, the deployment
              package includes the validated parameter set, research lineage,
              and expected performance envelope. Nothing is lost between
              research and monitoring.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The readiness checklist verifies that upstream gates are
              satisfied before allowing transition to live monitoring.
              This prevents deployment of strategies that have not completed
              the full validation pipeline.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Deployment includes:</p>
            <div className="space-y-2 mb-6">
              {[
                'Validated parameter set with stability metadata',
                'Performance envelope from walk-forward baseline',
                'Research version history and provenance chain',
                'Risk classification and expected drawdown profile',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <DeploymentReadinessMock />
        </div>
      </section>

      {/* ════════════════ 8. CONTINUOUS MONITORING ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <AdaptiveFlowMonitorMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 08
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Continuous validation closes the feedback loop
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Adaptive Flow monitors deployed strategies using the same
              statistical framework applied during research. Live performance
              is continuously compared against the walk-forward baseline to
              detect structural drift early.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              When degradation is confirmed, the system triggers a
              controlled re-optimization cycle. The new parameters replace
              the deployed set, and monitoring restarts. This creates a
              closed feedback loop between research and deployment.
            </p>

            <div className="space-y-2 mb-6">
              {[
                'Health scoring with confidence tracking',
                'Performance drift detection against validation baseline',
                'Compliance-gated re-optimization approval',
                'Full audit trail for every decision',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/features/continuous-validation" className="inline-flex items-center gap-1.5 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
              Continuous Validation details <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════ 9. CTA ════════════════ */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-[600px] mx-auto text-center relative z-10">
          <p className="text-[13px] text-gray-400/70 font-mono tracking-wide uppercase mb-4">
            A complete strategy research lifecycle
          </p>
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
            From idea to continuously validated deployment
          </h2>
          <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
            Every stage is designed to eliminate a specific class of failure.
            Strategies that reach deployment have survived parameter
            perturbation, cross-asset testing, out-of-sample evaluation,
            and continuous monitoring.
          </p>
          <p className="text-[13px] text-gray-400/60 leading-[1.7] mb-10">
            8 pipeline stages. Pass/fail gates at every transition. Full audit trail.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
          <div className="mt-4">
            <a href="#pipeline" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
              Review the pipeline stages <span className="text-gray-600">&rarr;</span>
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════ NAV FOOTER ════════════════ */}
      <section className="py-12 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
            Home
          </Link>
          <Link to="/features/workspace" className="inline-flex items-center gap-2 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
            Next: Research Workspace
            <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ResearchPipelinePage;
